import { EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'
import MainModule from '../../../modules/main'

export interface DropdownStatusEvent {
  status: 'show' | 'hide'
  modalId: string
}

export interface LvcDropdown {
  modalId: string
  modalStatus: string
  title: string
  titleSize: string
  fullscreen: boolean
  onToggle: string
}

export class LvcDropdown extends MinzeElement {
  attrs = [
    'modal-id',
    'title',
    'max-width',
    'title-size',
    'flex-grow',
    'min-width',
    'width',
    'background',
    'fullscreen',
    'on-toggle',
    'no-overlay',
  ]
  reactive: Reactive = [['modalStatus', 'hide']]
  static observedAttributes = ['on-toggle']

  toggleDropdown = (event: Event) => {
    const detail = (event as CustomEvent<DropdownStatusEvent>).detail
    if (this.modalId === detail.modalId) {
      if (!detail.status) detail.status = this.modalStatus === 'hide' ? 'show' : 'hide'
      if (detail.status === 'show') {
        this.modalStatus = 'show'
        Callback.call(this.onToggle ?? '', true)
      } else {
        this.modalStatus = 'hide'
        Callback.call(this.onToggle ?? '', false)
      }
    }
  }

  closeDropdown = (event: Event) => {
    MainModule.getInstance().hideModal(this.modalId)
  }

  clickContainer = (event: Event) => {
    MainModule.getInstance().hideModal(this.modalId)
  }

  html = () => {
    let overlay = `<div id="lvc-dropdown-overlay" class="lvc-dropdown-overlay ${this.modalStatus}"></div>`
    if (this.noOverlay) {
      overlay = ''
    }
    return `
      ${overlay}
      <div style="position: relative" class="lvc-dropdown-container">
          <slot part="trigger" name="lvc-dropdown-trigger">
          </slot>
          <div id="lvc-dropdown" class="lvc-dropdown ${this.modalStatus}">
          <!-- Dropdown content -->
          <div part="dropdown-content" class="lvc-dropdown-content">
            <slot name="lvc-dropdown-content"></slot>
          </div>
      </div>
    `
  }
  css = () => `
    :host {
        display: flex;
        ${this.flexGrow ? `flex: ${this.flexGrow};` : ''};

    }
    
    .lvc-dropdown-overlay {
      position: fixed; /* Stay in place */
      z-index: 1; /* Sit on top */
      left: 0;
      top: 0;
      width: 100%; /* Full width */
      height: 100%; /* Full height */
      overflow: auto; /* Enable scroll if needed */
      opacity: 0.4
    }

    .lvc-dropdown-overlay.show {
      display: block
    }

    .lvc-dropdown-overlay.hide {
      display: none
    }

    .lvc-dropdown-container {
        min-width: ${this.minWidth || 'none'};
        width: 100%;
        max-width: ${this.maxWidth || 'none'};
    }

    .lvc-dropdown {
      display: flex;
      justify-content: center;
      position: absolute;

      z-index: 1401;
      left: 50%;
      transform: translateX(-50%);
    }

    .lvc-dropdown.show {
      display: flex
    }

    .lvc-dropdown.hide {
      display: none
    }

    .lvc-dropdown-content {
      margin: auto;
      width: 100%;
      position: relative;
      height: 100%;
      background: ${this.background ? this.background : '#fff'};
      border-radius: 30px;
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
      min-width: 362px;
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      padding: 16px;
      gap: 8px;
      top: 37px;
    }

    .lvc-dropdown-close {
      color: #fff;
      font-size: 28px;
      font-weight: ${Font.weight.bold};
      position: absolute;
      top: 45px;
      right: 16px;
    }

    .lvc-dropdown-close:hover,
    .lvc-dropdown-close:focus {
      color: #aaaaaa;
      text-decoration: none;
      cursor: pointer;
    }

    .lvc-dropdown-title {
      text-transform: uppercase;
    }

    ::slotted([slot=lvc-dropdown-content]) {
      background: ${this.background ? this.background : '#fff'};
    }

    @media only screen and (min-width: 300px) and (max-width: 620px){
      .lvc-dropdown {
        width: 100%;
        height: 100%;
      }
    }

    @media only screen and (min-width: 621px) and (max-width: 960px){
      .lvc-dropdown {
        width: 100%;
        height: 100%;
      }


      .lvc-dropdown-content {
        max-width: 540px;
      }
    }

    @media only screen and (min-width: 960px) and (max-width: 1366px){
      .lvc-dropdown {
        width: 100%;
      }

      .lvc-dropdown-content {
        max-width: 540px;
      }
    }

    @media only screen and (min-width: 1366px) {
      .lvc-dropdown {
        width: 100%;
      }

      .lvc-dropdown-content {
        max-width: 540px;
      }
    }

    ${
      this.fullscreen
        ? `
      @media screen and (max-width: 730px) {
        .lvc-dropdown {
          position: fixed;
          top: 0;
          bottom: 0;
        }
      }
    `
        : ''
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:toggle-modal', this.toggleDropdown],
    ['.lvc-dropdown-overlay', 'click', this.clickContainer],
  ]
}
